import axios from 'axios';
import type { Class } from 'type-fest';
import { onApiError } from './errors';
import { API } from '../../config';

const axiosInstance = axios.create({
  baseURL: API,
});

export interface IRequestOptions {
  headers?: Dict;
  baseURL?: string;
  responseType?: string;
}

type MethodType = 'get' | 'delete' | 'put' | 'post' | 'patch';
export interface IRequestConfig {
  method?: MethodType;
  headers?: Dict;
  url?: string;
  data?: Dict | string;
  params?: Dict | string;
}

function toFormData(data?: Dict | string) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  return Object.keys(data)
    .map((key) => {
      return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
    })
    .join('&');
}

async function axiosCall(
  configs: IRequestConfig,
  resolve: (p: any) => void,
  reject: (p: any) => void
): Promise<any> {
  return axiosInstance
    .request(configs)
    .then((res) => {
      resolve(res.data);
    })
    .catch((err) => {
      reject(err);
    });
}

export function getConfigs(
  method: MethodType,
  contentType: string,
  url: string,
  options: Dict
): IRequestConfig {
  const configs: IRequestConfig = {
    ...options,
    method,
    url,
    headers: {
      ...options.headers,
      'content-type': contentType,
    },
  };
  return configs;
}

export const apiCall =
  <T>(Model: Class<T>) =>
  async (
    method: MethodType,
    url: string,
    params?: Dict,
    options: IRequestOptions = {}
  ) => {
    return new Promise<T>((resolve, reject) => {
      const configs: IRequestConfig = getConfigs(
        method,
        'application/x-www-form-urlencoded',
        url,
        options
      );

      if (method === 'get' || method === 'delete') {
        configs.params = params;
      } else {
        configs.data = toFormData({ ...params, property: 2 });
      }

      axiosCall(
        configs,
        (response: Dict) => {
          const obj: T = new Model(response);
          resolve(obj);
        },
        reject
      );
    }).catch((err) => {
      throw onApiError(url, params, err);
    });
  };

export function setBaseEndpoint(endpoint: string): void {
  const baseURL = endpoint.endsWith('/') ? endpoint : endpoint + '/';
  axiosInstance.defaults.baseURL = baseURL;
}

export function getBaseEndpoint(): string {
  return axiosInstance.defaults.baseURL || '';
}

export function setHttpsAgent(httpsAgent: unknown): void {
  axiosInstance.defaults.httpsAgent = httpsAgent;
}
